/**
 * Which of a study's documents carry an open rename notice (plan step 2).
 *
 * The older "the data dictionary changed" line on a card reads this: beside a
 * rename notice it points to the notice (which offers three choices) instead
 * of telling the person to regenerate. One listener per page, not per card.
 */
import { useEffect, useState } from 'react';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { documentKeyOf } from './renameNoticeText';

export function useOpenRenameNotices(projectId: string | undefined): ReadonlySet<string> {
  const [keys, setKeys] = useState<ReadonlySet<string>>(new Set());
  useEffect(() => {
    if (!projectId) return;
    const unsub = onSnapshot(
      collection(db, 'projects', projectId, 'renameNotices'),
      (snap) => setKeys(new Set(snap.docs.map((d) => d.id))),
      () => setKeys(new Set()),
    );
    return () => unsub();
  }, [projectId]);
  return keys;
}

/** Whether ONE document carries an open rename notice — for a row that renders alone. */
export function useRenameNoticeOpen(projectId: string | undefined, documentPath: string): boolean {
  const [open, setOpen] = useState(false);
  const key = documentKeyOf(documentPath);
  useEffect(() => {
    if (!projectId) return;
    const unsub = onSnapshot(
      doc(db, 'projects', projectId, 'renameNotices', key),
      (snap) => setOpen(snap.exists()),
      () => setOpen(false),
    );
    return () => unsub();
  }, [projectId, key]);
  return open;
}
